import { FC, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { BrokerSummary, TraderGrantDto } from "../../../api/Api";
import { useBrokerOverview } from "../../brokers/dataHooks/brokerQueries";
import {
  useBrokerTraderGrants,
  useGrantBrokerTraderAccess,
  useRevokeBrokerTraderAccess,
  useUpdateBrokerTraderAccessExpiry
} from "./queries";
import { GrantDialog } from "../../../components/traderAccess/grantDialog";
import { GrantsTable } from "../../../components/traderAccess/grantsTable";
import { ExtendExpiryDialog } from "../../../components/traderAccess/extendExpiryDialog";
import { RevokeConfirmDialog } from "../../../components/traderAccess/revokeConfirmDialog";

export const BrokerTraderAccessAdmin: FC = () => {
  const [selectedBroker, setSelectedBroker] = useState<BrokerSummary | null>(
    null
  );
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [extendTarget, setExtendTarget] = useState<TraderGrantDto | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<TraderGrantDto | null>(null);

  const brokersQuery = useBrokerOverview("active", "include");
  const brokerOptions = useMemo(
    () => brokersQuery.data ?? [],
    [brokersQuery.data]
  );

  const brokerId = selectedBroker?.id;

  const grantsQuery = useBrokerTraderGrants(brokerId, includeRevoked);
  const grants = grantsQuery.data ?? [];

  const grantMutation = useGrantBrokerTraderAccess({
    onSuccess: () => setGrantOpen(false)
  });
  const revokeMutation = useRevokeBrokerTraderAccess({
    onSuccess: () => setRevokeTarget(null)
  });
  const updateExpiryMutation = useUpdateBrokerTraderAccessExpiry({
    onSuccess: () => setExtendTarget(null)
  });

  const handleClearExpiry = (row: TraderGrantDto) => {
    if (!row.entityId || !row.userObjectId) return;
    updateExpiryMutation.mutate({
      entityId: row.entityId,
      userObjectId: row.userObjectId,
      expiresAt: null
    });
  };

  const handleExtendSubmit = (expiresAt: string | null) => {
    if (!extendTarget?.entityId || !extendTarget.userObjectId) return;
    updateExpiryMutation.mutate({
      entityId: extendTarget.entityId,
      userObjectId: extendTarget.userObjectId,
      expiresAt
    });
  };

  const handleRevokeConfirm = () => {
    if (!revokeTarget?.entityId || !revokeTarget.userObjectId) return;
    revokeMutation.mutate({
      entityId: revokeTarget.entityId,
      userObjectId: revokeTarget.userObjectId
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minHeight: 0
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Autocomplete<BrokerSummary>
          sx={{ minWidth: 460 }}
          multiple={false}
          options={brokerOptions}
          value={selectedBroker}
          onChange={(_, next) => setSelectedBroker(next)}
          getOptionLabel={(option) =>
            option.brokerName
              ? `${option.brokerName} - ${option.tradingTeam ?? ""}`
              : ""
          }
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderValue={(v) => {
            return (
              <Box
                display="flex"
                gap={3}
                alignItems="center"
                paddingX="8px"
                height="100%"
              >
                <Typography variant="body1">{v?.brokerName}</Typography>-
                <Typography variant="body1">{v?.tradingTeam}</Typography>
              </Box>
            );
          }}
          loading={brokersQuery.isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Broker"
              placeholder={selectedBroker ? "" : "Select a broker"}
            />
          )}
        />

        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeRevoked}
                onChange={(e) => setIncludeRevoked(e.target.checked)}
              />
            }
            label="Show revoked"
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            disabled={!brokerId}
            onClick={() => setGrantOpen(true)}
          >
            Grant access
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {brokerId ? (
          <GrantsTable
            rows={grants}
            loading={grantsQuery.isLoading || grantsQuery.isFetching}
            onExtendExpiry={setExtendTarget}
            onClearExpiry={handleClearExpiry}
            onRevoke={setRevokeTarget}
          />
        ) : (
          <Box sx={{ padding: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Select a broker to manage trader access.
            </Typography>
          </Box>
        )}
      </Box>

      {brokerId && (
        <GrantDialog
          open={grantOpen}
          entityId={brokerId}
          onClose={() => setGrantOpen(false)}
          onSubmit={(body) => grantMutation.mutate(body)}
          isSubmitting={grantMutation.isPending}
        />
      )}

      <ExtendExpiryDialog
        open={extendTarget !== null}
        grant={extendTarget}
        onClose={() => setExtendTarget(null)}
        onSubmit={handleExtendSubmit}
        isSubmitting={updateExpiryMutation.isPending}
      />

      <RevokeConfirmDialog
        open={revokeTarget !== null}
        grant={revokeTarget}
        entityLabel="broker"
        onCancel={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirm}
        isSubmitting={revokeMutation.isPending}
      />
    </Box>
  );
};
